import { useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { isAxiosError } from 'axios';
import { addDays } from 'date-fns';
import { ArrowLeft, CircleUser } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { api } from '@/lib/api.ts';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { userQueryOptions } from '@/hooks/queries/users.ts';
import type { APIParkUserDetail } from '@/types/index.ts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { DatePicker, toDateValue } from '@/components/date-picker.tsx';
import { PageContainer } from '@/components/page-container.tsx';
import { PasswordInput } from '@/components/password-input';
import { NrgkickSection } from '@/components/user-edit/nrgkick-section.tsx';
import { RfidSection } from '@/components/user-edit/rfid-section.tsx';
import { parseUserId } from './+utils/parse-user-id.ts';

export const Route = createFileRoute('/_logged/users/$id/edit')({
  component: UserEditPage,
  staticData: {
    label: msg({
      comment:
        'Route label of the user edit page (/users/{id}/edit) — breadcrumb leaf after the username and document title. Used as a noun phrase for the editing view, not an imperative button.',
      message: 'Edit',
    }),
  },
  // The `$id` layout loader primes the same query, but loaders run in
  // parallel, so this one revalidates the param and the fetch outcome on its
  // own instead of assuming the parent already did.
  loader: async ({ context, params }) => {
    const userId = parseUserId(params.id);
    if (userId == null) throw redirect({ to: '/users' });
    let user: APIParkUserDetail;
    try {
      user = await context.queryClient.ensureQueryData(userQueryOptions(userId));
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404 || status === 403) throw redirect({ to: '/users' });
      throw error;
    }
    if (user.is_readonly) throw redirect({ to: '/users/$id', params: { id: params.id } });
  },
});

/**
 * The mutation counterpart of the read-only `/users/$id` hub: account fields,
 * plus the NRGKick and RFID sections when the admin has those features.
 * Expired users cannot be edited — the loader bounces them back to the hub
 * (and the hub hides its edit button for them). The signed-in admin edits
 * themselves on `/account/edit` instead.
 */
function UserEditPage() {
  const { t } = useLingui();
  const { id } = Route.useParams();
  const { data: user } = useSuspenseQuery(userQueryOptions(Number(id)));
  const currentUser = useCurrentUser();
  const nrgkickEnabled = currentUser.features.parkadmin?.feat_nrgkick ?? false;
  const rfidEnabled = currentUser.features.parkadmin?.feat_rfid ?? false;

  return (
    <PageContainer>
      <header className='flex items-center gap-4'>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant='outline'
                size='icon'
                nativeButton={false}
                aria-label={t`Back to the user dashboard`}
                render={<Link to='/users/$id' params={{ id }} />}
              >
                <ArrowLeft />
              </Button>
            }
          />
          <TooltipContent>
            <Trans>Back to the user dashboard</Trans>
          </TooltipContent>
        </Tooltip>
        <div className='space-y-1'>
          <p className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
            <Trans comment='Small eyebrow heading above the username on the user edit page.'>
              Edit user
            </Trans>
          </p>
          <h1 className='text-3xl font-semibold tracking-tight'>{user.username}</h1>
        </div>
      </header>

      <div className='grid gap-6 md:grid-cols-2'>
        {/* key: the card's form state is seeded from `user` at mount only — without a remount, an in-SPA switch to another user would keep the previous user's draft. */}
        <UserAccountCard key={user.id} user={user} />
        {rfidEnabled ?
          // The near-empty relative cell keeps the RFID card out of the
          // row-height computation on md+, clamping it to the Account card's
          // height so a long card list scrolls inside instead of stretching
          // the row. The min-h is the floor the RFID card needs for its header,
          // add form and a few list rows, should the Account card ever shrink
          // below it.
          <div className='relative md:min-h-80'>
            <RfidSection userId={user.id} className='md:absolute md:inset-0' />
          </div>
        : null}
        {nrgkickEnabled ?
          <NrgkickSection userId={user.id} />
        : null}
      </div>
    </PageContainer>
  );
}

type EditUserBody = {
  email?: string;
  new_password?: string;
  scheduled_expire_time?: string | null;
};

type AccountFormValues = {
  email: string;
  newPassword: string;
  expiresOn: string;
};

function isFutureDate(value: string): boolean {
  return new Date(value).getTime() > Date.now();
}

/**
 * Account fields, prefilled from the detail query (decision 21): email and
 * expiration show their current values; the password stays blank behind a
 * "Change password?" switch. `PATCH parkadmin/current/users/{id}/` sends only
 * the fields that differ from the loaded values — an emptied email is treated
 * as "unchanged", so clearing an email stays impossible, as before. After a
 * save the saved values become the new comparison baseline, so Save disables
 * again until something else changes.
 */
function UserAccountCard({ user }: { user: APIParkUserDetail }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [changePassword, setChangePassword] = useState(false);
  const [noExpiration, setNoExpiration] = useState(user.scheduled_expire_time === null);
  const [defaults, setDefaults] = useState(() => ({
    email: user.email,
    noExpiration: user.scheduled_expire_time === null,
    expiresOn:
      user.scheduled_expire_time === null ? '' : toDateValue(new Date(user.scheduled_expire_time)),
  }));

  const editUserSchema = z.object({
    email: z.union([z.literal(''), z.email(t`Enter a valid email address`)]),
    newPassword: z.union([
      z.literal(''),
      z
        .string()
        .min(4, t`Must be at least 4 characters`)
        .max(128, t`Password is too long`),
    ]),
    // The prefilled (unchanged) date is exempt from the future check: it is
    // never submitted, and it may legitimately be today.
    expiresOn: z.union([
      z.literal(''),
      z
        .string()
        .refine(
          (value) => value === defaults.expiresOn || isFutureDate(value),
          t`Pick a future date`,
        ),
    ]),
  });

  function changedFields(value: AccountFormValues): EditUserBody {
    const body: EditUserBody = {};
    if (value.email !== '' && value.email !== defaults.email) body.email = value.email;
    if (changePassword && value.newPassword !== '') body.new_password = value.newPassword;
    if (noExpiration) {
      if (!defaults.noExpiration) body.scheduled_expire_time = null;
    } else if (
      value.expiresOn !== '' &&
      (defaults.noExpiration || value.expiresOn !== defaults.expiresOn)
    ) {
      body.scheduled_expire_time = new Date(value.expiresOn).toISOString();
    }
    return body;
  }

  const mutation = useMutation({
    mutationFn: (body: EditUserBody) => api.patch(`parkadmin/current/users/${user.id}/`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: userQueryOptions(user.id).queryKey });
    },
  });

  const form = useForm({
    defaultValues: {
      email: defaults.email,
      newPassword: '',
      expiresOn: defaults.expiresOn,
    },
    validators: { onSubmit: editUserSchema },
    onSubmit: async ({ value }) => {
      const body = changedFields(value);
      try {
        await mutation.mutateAsync(body);
        toast.success(t`User updated.`);
        const nextDefaults = {
          email: body.email ?? defaults.email,
          noExpiration,
          expiresOn: noExpiration ? '' : value.expiresOn || defaults.expiresOn,
        };
        setDefaults(nextDefaults);
        setChangePassword(false);
        form.reset({
          email: nextDefaults.email,
          newPassword: '',
          expiresOn: nextDefaults.expiresOn,
        });
      } catch {
        toast.error(t`Could not update the user. Please try again.`);
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <CircleUser className='size-5' />
          <Trans comment='Title of the card on the user edit page holding the account fields (email, password, expiration).'>
            Account
          </Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field name='email'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Email</Trans>
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type='email'
                    autoComplete='email'
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <div className='flex items-center gap-2'>
              <Switch
                id='change-password'
                checked={changePassword}
                onCheckedChange={(checked) => {
                  setChangePassword(checked);
                  // Clear any typed password when opting out, so it can never be
                  // submitted or trip validation — guards against an accidental reset.
                  if (!checked) form.setFieldValue('newPassword', '');
                }}
              />
              <Label htmlFor='change-password'>
                <Trans comment='Label for a toggle switch on the user edit page. When off, the New password field below is disabled and cleared, preventing an accidental password reset. Phrased as a yes/no question (Change password?).'>
                  Change password?
                </Trans>
              </Label>
            </div>

            <form.Field name='newPassword'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>New password</Trans>
                  </FieldLabel>
                  <PasswordInput
                    id={field.name}
                    name={field.name}
                    autoComplete='new-password'
                    disabled={!changePassword}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>

            <div className='flex items-center gap-2'>
              <Switch id='no-expiration' checked={noExpiration} onCheckedChange={setNoExpiration} />
              <Label htmlFor='no-expiration'>
                <Trans>No expiration</Trans>
              </Label>
            </div>

            <form.Field name='expiresOn'>
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>
                    <Trans>Expiration date</Trans>
                  </FieldLabel>
                  <DatePicker
                    id={field.name}
                    disabled={noExpiration}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}
                    aria-invalid={field.state.meta.errors.length > 0}
                    // A new expiration must be a future day: tomorrow is the
                    // first selectable date (the prefilled current date may
                    // predate it — it stays valid only while unchanged).
                    startDate={toDateValue(addDays(new Date(), 1))}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
          </FieldGroup>

          <div className='mt-6 flex justify-end'>
            <form.Subscribe
              selector={(state) =>
                [
                  state.values.email,
                  state.values.newPassword,
                  state.values.expiresOn,
                  state.isSubmitting,
                ] as const
              }
            >
              {([email, newPassword, expiresOn, isSubmitting]) => {
                const hasChanges =
                  Object.keys(changedFields({ email, newPassword, expiresOn })).length > 0;
                return (
                  <Button type='submit' disabled={!hasChanges || isSubmitting}>
                    {isSubmitting ? t`Saving…` : t`Save`}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
